import { Crs } from "@evergis/sgis/es/Crs";
import { projectRings } from "@evergis/sgis/es/geotools";
import { Coordinates } from "@evergis/sgis/es/baseTypes";
import { Render } from "@evergis/sgis/es/renders/Render";
import { simplifyCoordinates } from "@evergis/sgis/es/utils/math";
import { LineEndingType } from "@evergis/api";

export type MiterPosition = "left" | "right";

export interface LineMiterRenderBaseInfo {
  position: MiterPosition;
  color: string;
  strokeWidth: number;
  kind: LineEndingType;
  size: number;
}

const adaptMiterRings = (rings: Coordinates[][], resolution: number, fromCrs: Crs, toCrs: Crs) => {
  const projected = projectRings(rings, fromCrs, toCrs);

  return simplifyCoordinates(
    projected.map(ring => ring.map(([x, y]) => [x / resolution, y / -resolution])),
    1,
  );
};

const calcMiterSize = (size: number, strokeWidth: number) => {
  return 0.5 * size * (strokeWidth + 1);
};

export abstract class LineMiterRender implements LineMiterRenderBaseInfo {
  position: MiterPosition;

  color: string;

  strokeWidth: number;

  kind: LineEndingType;

  size: number;

  constructor(baseInfo: LineMiterRenderBaseInfo) {
    Object.assign(this, baseInfo);
  }

  getAnchor = (rings: Coordinates[][]): Coordinates => {
    if (this.position === "right") {
      const lastRing = rings[rings.length - 1];

      return (lastRing && lastRing[lastRing.length - 1]) || null;
    }

    const firstRing = rings[0];

    return (firstRing && firstRing[0]) || null;
  };

  getRender(anchorRings: Coordinates[][], anchorCrs: Crs, resolution: number, currentCrs: Crs): Render {
    const xMultiplier = this.position === "right" ? 1 : -1;
    const miterAnchor = this.getAnchor(anchorRings);
    const miterRings = this.createRings(miterAnchor, xMultiplier);
    const projectedMiterRings = adaptMiterRings(miterRings, resolution, anchorCrs, currentCrs);

    return this.createRender(projectedMiterRings);
  }

  get miterSize() {
    return calcMiterSize(this.size, this.strokeWidth);
  }

  abstract get usageSize(): number;

  abstract createRings(anchor: Coordinates, xMultiplier: number): Coordinates[][];

  abstract createRender(rings: Coordinates[][]): Render;
}
